/**
 * Eraya price-test bridge for this theme (Halo / "Ella").
 *
 * The SDK rewrites prices once, on load. This theme keeps rewriting them after
 * that, from four places, so each one has to hand the price back:
 *
 *   1. Card colour swatch click -> assets/theme.js rewrites the card price from
 *      data-json-product and never touches data-variant-id.
 *   2. PDP variant change -> assets/variants.js morphs #product-price-{id} with
 *      the ORIGINAL price from the section render.
 *   3. Quick view variant change -> variants-quick-view.js morphs
 *      #product-quick-view-price-{id}.
 *   4. AJAX cards (recommendations, recently viewed, predictive search) that
 *      the SDK never saw.
 *
 * This theme also renders price and compare-at as two sibling blocks
 * (.price__regular / .price__sale) and toggles them with .price--on-sale, so a
 * text-only rewrite can land in a hidden block. Everything below writes the
 * whole visible price block from the engine's own numbers.
 *
 * Display only. What the shopper is CHARGED comes from the *_eraya_* line-item
 * properties the SDK stamps onto /cart/add.
 */
(function () {
  'use strict';

  if (window.__erayaPriceSyncInit) return;
  window.__erayaPriceSyncInit = true;

  function manager() {
    try {
      var E = window.Eraya;
      if (E && E.sdk && typeof E.sdk.getPriceTestManager === 'function') {
        return E.sdk.getPriceTestManager();
      }
    } catch (_) {}
    return null;
  }

  function call(m, name, args) {
    if (!m || typeof m[name] !== 'function') return null;
    try {
      var out = m[name].apply(m, args || []);
      return out == null ? null : out;
    } catch (_) {
      return null;
    }
  }

  function testPrice(variantId) {
    if (!variantId) return null;
    var cents = call(manager(), 'getPriceByVariantId', [variantId]);
    return typeof cents === 'number' ? cents : null;
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.money_format || '${{amount}}');
    }
    return (cents / 100).toFixed(2);
  }

  // Writing the same string again would still be a DOM mutation, and the
  // morph observer below reacts to mutations -- that is how a naive bridge
  // ends up in an endless write/observe loop. So only write on a real change.
  function setText(el, text) {
    if (!el || el.textContent === text) return false;
    el.textContent = text;
    return true;
  }

  /* ── The visible price block ------------------------------------------ */
  // The test's compare-at wins only when it is above the price being shown;
  // otherwise the product's own compare-at stands.
  function pickCompare(nativeCompare, testCompare, price) {
    if (typeof testCompare !== 'number' || testCompare <= price) testCompare = 0;
    return testCompare > nativeCompare ? testCompare : nativeCompare;
  }

  function percentOff(compare, price) {
    return compare > 0 ? Math.round(((compare - price) * 100) / compare) : 0;
  }

  // Some call sites wrap the amount in <span class="money"> (the currency
  // converter's marker). Write inside it when it is there, so the theme's own
  // currency switching keeps working.
  function moneyNode(el) {
    return (el && el.querySelector('.money')) || el;
  }

  function syncPriceRoot(root) {
    var variantId = root.getAttribute('data-variant-id');
    var price = testPrice(variantId);
    if (price == null) return false;

    var productId = root.getAttribute('data-product-id');
    var compareNode = root.querySelector('.price__compare');
    var regular = root.querySelector('.price__regular .price-item');
    var sale = root.querySelector('.price__sale .price-item--sale');
    // Some call sites (lookbook overlay) render only one of the two blocks.
    if (!regular && !sale) return false;

    var nativeCompare = parseInt(compareNode && compareNode.getAttribute('data-compare'), 10);
    if (isNaN(nativeCompare) || nativeCompare <= 0) nativeCompare = 0;
    var testCompare = call(manager(), 'getCompareAtPriceForProduct', [productId, variantId]);
    var compare = pickCompare(nativeCompare, testCompare, price);
    var discounted = compare > price;

    root.classList.toggle('price--on-sale', discounted);

    if (regular) setText(moneyNode(regular), formatMoney(price));
    if (sale) setText(moneyNode(sale), formatMoney(price));

    if (compareNode) {
      var strike = compareNode.querySelector('.price-item');
      if (discounted) {
        compareNode.setAttribute('data-compare', compare);
        if (strike) setText(moneyNode(strike), formatMoney(compare));
      } else if (strike) {
        setText(moneyNode(strike), '');
      }
    }

    var last = root.querySelector('.price__sale .price__last');
    if (last) last.setAttribute('data-last', price);

    var percent = percentOff(compare, price);
    var percentLabel = root.querySelector('.label_sale');
    if (percentLabel && percent > 0) {
      // theme.js replaces this node's HTML on card swatch change, so the
      // isolated value span is not always there to write into.
      var percentValue = percentLabel.querySelector('.label_sale_value');
      if (percentValue) setText(percentValue, percent + '%');
      else setText(percentLabel, '(-' + percent + '%)');
    }
    var savedValue = root.querySelector('.price-saved-value');
    if (savedValue && discounted) setText(savedValue, formatMoney(compare - price));

    // Sale stickers live outside .price (card corner, .productView-badge). Only
    // touch ones that already show a discount; the "Sale" text badge must stay
    // a word. The badge scripts read data-compare/data-last, updated above.
    if (discounted) {
      var scopeEl = root.closest('.card') || root.closest('.productView');
      if (scopeEl) {
        scopeEl.querySelectorAll('.sale-badge').forEach(function (badge) {
          if (!/%|-\s*\d/.test(badge.textContent)) return;
          setText(badge, badge.textContent.replace(/-?\s*\d+\s*%/, '-' + percent + '%'));
        });
      }
    }
    return true;
  }

  // Custom header (search overlay, mega menu, mobile drawer), the related
  // slider and the custom card sections (best sellers, for him/her, ...): a bare amount,
  // optionally with a strike-through sibling. The strike is only rendered when
  // the native compare-at beats the price; hide it if the test price overtakes it.
  // ponytail: a test compare-at can't add a strike where none was rendered.
  // These widgets format money themselves; some drop ".00" (Rs.3,989). Keep
  // whichever style the node already shows so a rewrite doesn't stand out.
  function plainMoney(cents, current) {
    var text = formatMoney(cents);
    return /[.,]\d{2}\s*$/.test(current) ? text : text.replace(/[.,]00(\s*)$/, '$1');
  }

  function syncPlainPrice(el) {
    var variantId = el.getAttribute('data-variant-id');
    var price = testPrice(variantId);
    if (price == null) return;
    setText(el, plainMoney(price, el.textContent));

    var strike = el.parentNode && el.parentNode.querySelector('.eraya-plain-compare');
    if (!strike) return;
    var testCompare = call(manager(), 'getCompareAtPriceForProduct', [el.getAttribute('data-product-id'), variantId]);
    var compare = pickCompare(parseInt(strike.getAttribute('data-compare'), 10) || 0, testCompare, price);
    // "from <s>X</s>" cards: hide the whole label, not just the struck amount.
    var shown = strike.closest('.eraya-plain-compare-wrap') || strike;
    if (compare > price) {
      shown.style.display = '';
      setText(strike, plainMoney(compare, strike.textContent));
    } else {
      shown.style.display = 'none';
    }
  }

  // Quick-add dropdowns label each option "50ml - Rs.3,989.00"; the option's
  // value is the variant id, data-eraya-option holds the label prefix.
  function syncOptionLabel(option) {
    var price = testPrice(option.value);
    if (price == null) return;
    var prefix = option.getAttribute('data-eraya-option');
    var current = option.textContent.trim();
    setText(option, prefix + plainMoney(price, current));
  }

  // `scope` keeps a swatch click from restamping every card of that product
  // elsewhere on the page (grid + recommendations can show it twice).
  function syncProduct(productId, variantId, scope) {
    if (!productId) return false;
    var roots = (scope || document).querySelectorAll('.price[data-product-id="' + productId + '"]');
    if (!roots.length) return false;
    var applied = false;
    roots.forEach(function (root) {
      if (variantId) root.setAttribute('data-variant-id', variantId);
      if (syncPriceRoot(root)) applied = true;
    });
    return applied;
  }

  // Only prices we can attribute to an exact variant. Range ("From $X") cards
  // stay untouched: one variant's test price must not replace a range.
  function syncAll() {
    document.querySelectorAll('.price[data-product-id][data-variant-id]').forEach(syncPriceRoot);
    document.querySelectorAll('.eraya-plain-price[data-variant-id]').forEach(syncPlainPrice);
    document.querySelectorAll('option[data-eraya-option]').forEach(syncOptionLabel);
    syncStickyBars();
  }

  /* ── 1. Card swatch --------------------------------------------------- */
  // theme.js rewrites this card's price from JSON (original prices) on click.
  document.addEventListener(
    'click',
    function (event) {
      var label = event.target && event.target.closest
        ? event.target.closest('.card .swatch-label[data-variant-id]')
        : null;
      if (!label) return;
      var card = label.closest('.product-item[data-product-id]');
      if (!card) return;
      var productId = card.getAttribute('data-product-id');
      var variantId = label.getAttribute('data-variant-id');
      card.setAttribute('data-variant-id', variantId);
      // theme.js writes the price inside this same click; run after it.
      setTimeout(function () { syncProduct(productId, variantId, card); }, 0);
    },
    true
  );

  /* ── 2 & 3. Containers the theme morphs ------------------------------- */
  // The observer callback runs before paint, so the original price is replaced
  // without a visible flash. Our own writes produce at most one extra callback
  // that then writes nothing (setText no-ops), so the loop stops by itself.
  function morphTarget(node) {
    if (node.nodeType !== 1) return null;
    if (node.id && /^product-(quick-view-)?price-/.test(node.id)) return node;
    return node.closest
      ? node.closest('[id^="product-price-"], [id^="product-quick-view-price-"]')
      : null;
  }

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (mutations) {
      var hit = null;
      for (var i = 0; i < mutations.length && !hit; i++) {
        hit = morphTarget(mutations[i].target);
        if (!hit) {
          var added = mutations[i].addedNodes;
          for (var j = 0; j < added.length && !hit; j++) hit = morphTarget(added[j]);
        }
      }
      if (!hit) return;

      var productId = null;
      var root = hit.querySelector('.price[data-product-id]');
      if (root) productId = root.getAttribute('data-product-id');
      // Scope to the morphed container: the fresh markup already carries that
      // variant's ids, and cards for the same product elsewhere keep their own.
      if (productId) syncProduct(productId, null, hit);
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ── 4. AJAX-injected cards ------------------------------------------- */
  var rescanTimer = null;
  function hasCard(node) {
    if (node.nodeType !== 1) return false;
    if (node.hasAttribute && node.hasAttribute('data-product-id')) return true;
    return !!(node.querySelector && node.querySelector('[data-product-id]'));
  }

  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (!hasCard(added[j])) continue;
          clearTimeout(rescanTimer);
          rescanTimer = setTimeout(syncAll, 150);
          return;
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ── Sticky bars ------------------------------------------------------ */
  // Both sticky bars compute their price in JS from variant.price, so any text
  // the engine writes there is overwritten on the bar's next render. Feed them
  // the same number the engine uses. No stored "original": when a variant has
  // no test price the theme's own write is already correct, so leave it alone.
  function syncStickyBars() {
    document.querySelectorAll('[data-sticky-add-to-cart] .sticky-price').forEach(function (bar) {
      var price = testPrice(bar.getAttribute('data-variant-id'));
      if (price == null) return;
      // Some templates show qty x price here (window.subtotal.show), so match
      // whatever the bar itself is showing.
      if (window.subtotal && window.subtotal.show) {
        var qtyInput = bar.closest('[data-sticky-add-to-cart]').querySelector('input[name="quantity"]');
        var qty = parseInt(qtyInput && qtyInput.value, 10);
        if (qty > 1) price = price * qty;
      }
      var priceNode = bar.querySelector('.money-subtotal');
      if (priceNode) setText(priceNode, formatMoney(price));
    });

    document.querySelectorAll('.pa-mini-sticky-atc').forEach(function (bar) {
      var button = bar.querySelector('.pa-mini-atc-btn');
      var price = testPrice(button && button.getAttribute('data-variant-id'));
      if (price == null) return;
      var text = bar.querySelector('.pa-mini-atc-price');
      if (text) setText(text, formatMoney(price));
    });
  }

  // Both bars re-render their price from their own state on any variant or
  // quantity change, so sync after the theme's handlers have written theirs.
  var stickyTimer = null;
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input) return;
    if (input.name === 'id' && input.closest) {
      var bar = input.closest('[data-sticky-add-to-cart]');
      if (bar) {
        bar.querySelectorAll('.sticky-price').forEach(function (node) {
          node.setAttribute('data-variant-id', input.value);
        });
        var pidHolder = bar.querySelector('[data-product]');
        if (pidHolder) syncProduct(pidHolder.getAttribute('data-product'), input.value);
      }
    }
    clearTimeout(stickyTimer);
    stickyTimer = setTimeout(syncStickyBars, 60);
  });

  /* ── Native add-to-cart forms ----------------------------------------- */
  // The custom card sections (best sellers, for him/her, collection grid, ...)
  // post <form action="/cart/add"> as a plain page submit. The SDK stamps the
  // _eraya_* properties -- what makes checkout charge the test price -- by
  // patching fetch/XHR, which a plain submit never goes through. Resend those
  // posts through fetch, then land on /cart exactly as the native post would.
  // Forms the theme already handles in JS call preventDefault first (they run
  // before this bubble-phase listener), so they are left alone.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (event.defaultPrevented || !form || form.tagName !== 'FORM') return;
    if (!/\/cart\/add\/?$/.test(form.getAttribute('action') || '')) return;
    if (!manager() || typeof window.fetch !== 'function') return; // no SDK: native post
    event.preventDefault();

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: new FormData(form)
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (data && data.status) {
          alert(data.description || data.message || 'Could not add to cart');
          return;
        }
        window.location.href = (window.routes && window.routes.cart) || '/cart';
      })
      // form.submit() does not fire 'submit' again, so this cannot loop.
      .catch(function () { form.submit(); });
  });

  /* ── Ready ------------------------------------------------------------ */
  // Debug seam: the pure helpers, so they can be asserted in isolation.
  window.__erayaPriceSync = {
    pickCompare: pickCompare,
    percentOff: percentOff,
    plainMoney: plainMoney,
    syncAll: syncAll
  };

  // Our pass must land AFTER the engine's own init pass, so the numbers we
  // read per variant win. Bounded poll: the SDK is async and has no guaranteed
  // ready flag in every build.
  var bootTries = 0;
  function boot() {
    if (!manager() && bootTries++ < 40) {
      setTimeout(boot, 250);
      return;
    }
    syncAll();
    // The engine's own init pass can land after ours (it scans every
    // [data-product-id] and may pick a product-level ancestor as the
    // container). Two bounded re-syncs put our per-variant numbers last.
    setTimeout(syncAll, 800);
    setTimeout(syncAll, 2500);
  }
  if (window.ErayaReady) setTimeout(boot, 0);
  window.addEventListener('eraya:ready', function () { setTimeout(boot, 0); });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setTimeout(boot, 0); });
  } else {
    setTimeout(boot, 0);
  }
})();
